import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronLeft, Check, AlertTriangle, Globe, Settings, SkipForward, Map, CreditCard, Shield, Briefcase, Plus, BadgeCheck, FileText, Clock, Link2, Search, Rocket } from 'lucide-react';
import api from '../utils/api';
import { getPlaybookSteps, BRANCH_QUESTIONS } from './guidedData';

const ICONS = { globe: Globe, settings: Settings, skip: SkipForward, map: Map, card: CreditCard, shield: Shield, briefcase: Briefcase, plus: Plus, 'check-badge': BadgeCheck, file: FileText, clock: Clock, link: Link2, badge: BadgeCheck, search: Search, rocket: Rocket };

const LearnMore = ({ items }) => {
  const [open, setOpen] = useState(null);
  if (!items?.length) return null;
  return (
    <div className="mt-4 space-y-2">
      {items.map((it, i) => (
        <div key={i} className="rounded-lg border border-divider bg-black">
          <button onClick={() => setOpen(open === i ? null : i)} data-testid={`learn-more-${i}`}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-lime font-semibold text-left">
            {it.label}
            <ChevronDown size={16} className={`transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>
          {open === i && <p className="px-4 pb-3 text-sm text-light-gray leading-relaxed">{it.content}</p>}
        </div>
      ))}
    </div>
  );
};

const FlowDiagram = ({ total, current }) => (
  <div className="flex items-center justify-center gap-1 sm:gap-2 mb-8 overflow-x-auto" data-testid="wizard-flow-diagram">
    {Array.from({ length: total }).map((_, i) => (
      <React.Fragment key={i}>
        <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
          i < current ? 'bg-lime border-lime text-black' : i === current ? 'border-lime text-lime bg-lime/10' : 'border-divider text-gray'}`}>
          {i < current ? <Check size={16} /> : i + 1}
        </div>
        {i < total - 1 && <div className={`shrink-0 w-4 sm:w-8 h-0.5 ${i < current ? 'bg-lime' : 'bg-divider'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const ConnectAction = ({ provider, connection, onConnected }) => {
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  if (connection?.connected) {
    return (
      <div className="rounded-xl border border-lime bg-lime/10 p-5 text-center" data-testid={`${provider}-connected-badge`}>
        <Check size={28} className="text-lime mx-auto mb-2" />
        <p className="text-white font-bold">Connected{connection.account_name ? ` — ${connection.account_name}` : ''}</p>
      </div>
    );
  }
  const start = async () => {
    setState('loading');
    try {
      const { data } = await api.get(`/guided/connect/${provider}/start`);
      if (!data.configured) { setState('almost'); setMsg(data.message); return; }
      window.location.href = data.authorize_url;
    } catch { setState('idle'); }
  };
  return (
    <div className="text-center">
      <button onClick={start} disabled={state === 'loading'} data-testid={`connect-${provider}-btn`}
        className="w-full py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90 disabled:opacity-60 transition-colors">
        {state === 'loading' ? 'Opening…' : `Connect ${provider === 'google' ? 'Google' : 'Meta'}`}
      </button>
      {state === 'almost' && (
        <div className="mt-4 rounded-lg border border-orange/50 bg-orange/10 p-4 text-sm text-light-gray" data-testid={`${provider}-almost-ready`}>
          <p className="text-orange font-bold mb-1">Almost ready</p>
          <p>{msg}</p>
        </div>
      )}
    </div>
  );
};

export default function StepWizard({ playbookId, saved, connections, onFinish, onExit, refreshConnections }) {
  const branch = BRANCH_QUESTIONS[playbookId];
  const [path, setPath] = useState(saved?.extra?.path || null);
  const [step, setStep] = useState(saved?.step || 0);
  const [checked, setChecked] = useState(false);

  const steps = path || !branch ? getPlaybookSteps(playbookId, path) : [];
  const total = steps.length;

  useEffect(() => { setChecked(false); }, [step]);

  const save = (nextStep, completed = false) => {
    api.post('/guided/progress', { playbook_id: playbookId, step: nextStep, completed, extra: { path } }).catch(() => {});
  };

  const choosePath = (p) => {
    setPath(p);
    setStep(0);
    api.post('/guided/progress', { playbook_id: playbookId, step: 0, completed: false, extra: { path: p } }).catch(() => {});
  };

  if (branch && !path) {
    return (
      <div className="max-w-md mx-auto text-center" data-testid={`wizard-${playbookId}-branch`}>
        <h2 className="text-3xl font-bold text-white mb-8">{branch.question}</h2>
        <div className="space-y-3">
          <button onClick={() => choosePath('yes')} data-testid="branch-yes-btn"
            className="w-full py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90 transition-colors">{branch.yes}</button>
          <button onClick={() => choosePath('no')} data-testid="branch-no-btn"
            className="w-full py-4 rounded-xl border-2 border-lime text-lime font-black text-lg hover:bg-lime/10 transition-colors">{branch.no}</button>
        </div>
        <button onClick={onExit} className="mt-6 text-sm text-gray hover:text-white" data-testid="wizard-exit-btn">← Back to journey</button>
      </div>
    );
  }

  const s = steps[step];
  if (!s) return null;
  const Icon = ICONS[s.icon] || Globe;
  const isConnect = s.type === 'connect';
  const conn = isConnect ? connections?.[s.provider] : null;
  const isLast = step === total - 1;
  const canNext = isConnect ? true : checked;

  const next = () => {
    if (isLast) { save(step, true); onFinish(); }
    else { const n = step + 1; setStep(n); save(n); }
  };

  return (
    <div className="max-w-md mx-auto" data-testid={`wizard-${playbookId}`}>
      <div className="mb-2 flex items-center justify-between">
        <button onClick={onExit} className="text-sm text-gray hover:text-white flex items-center gap-1" data-testid="wizard-exit-btn"><ChevronLeft size={14} /> Journey</button>
        <p className="text-sm font-bold text-lime" data-testid="wizard-progress">Step {step + 1} of {total}</p>
      </div>
      <div className="h-2 rounded-full bg-divider mb-6 overflow-hidden">
        <div className="h-full bg-lime transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
      </div>

      <FlowDiagram total={total} current={step} />

      <div className="rounded-2xl border border-divider bg-card-bg p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-lime/10 border border-lime/30 flex items-center justify-center mx-auto mb-5">
          <Icon size={30} className="text-lime" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3" data-testid="step-title">{s.title}</h2>
        <p className="text-base text-light-gray leading-relaxed" data-testid="step-body">{s.body}</p>

        {s.warning && (
          <div className="mt-4 rounded-lg border border-orange/50 bg-orange/10 p-3 flex gap-2 text-left" data-testid="step-warning">
            <AlertTriangle size={18} className="text-orange shrink-0 mt-0.5" />
            <p className="text-sm text-light-gray">{s.warning}</p>
          </div>
        )}

        <LearnMore items={s.learnMore} />

        {isConnect ? (
          <div className="mt-6">
            <ConnectAction provider={s.provider} connection={conn} onConnected={refreshConnections} />
            <button onClick={next} data-testid="step-next-btn"
              className="mt-4 w-full py-3 rounded-xl border border-divider text-light-gray font-bold hover:border-lime/50 transition-colors">
              {conn?.connected ? 'Finish →' : "I'll connect later — Finish →"}
            </button>
          </div>
        ) : (
          <div className="mt-6">
            <label className="flex items-center justify-center gap-3 p-4 rounded-xl bg-black border border-divider cursor-pointer hover:border-lime/40 transition-colors" data-testid="step-done-label">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                className="w-5 h-5 accent-[#c6f24e]" data-testid="step-done-checkbox" />
              <span className="text-white font-semibold">✓ I did this</span>
            </label>
            <button onClick={next} disabled={!canNext} data-testid="step-next-btn"
              className="mt-3 w-full py-4 rounded-xl bg-lime text-black font-black text-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-lime/90 transition-colors">
              {isLast ? 'Finish →' : 'Next →'}
            </button>
          </div>
        )}

        {step > 0 && (
          <button onClick={() => { const p = step - 1; setStep(p); save(p); }} data-testid="step-back-btn"
            className="mt-3 text-sm text-gray hover:text-white">← Back</button>
        )}
      </div>
    </div>
  );
}
